import React, { useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  TextField,
  FormControlLabel,
  Switch,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Alert,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { CMSExploreCategory } from "../../../../services/exploreCategories";
import { HeaderCategory } from "../../../../services/headerCategories";
import { Subcategory, subcategoriesService } from "../../../../services/subcategories";
import { resolveExploreCategoryRoute } from "../../../../utils/categoryRoutes";
import ImageUpload from "../../../../components/ImageUpload";

interface ExploreCategoriesDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  editingExploreCategory: CMSExploreCategory | null;
  setEditingExploreCategory: React.Dispatch<React.SetStateAction<CMSExploreCategory | null>>;
  headerCategories?: HeaderCategory[];
  subcategories?: Subcategory[];
}

const ExploreCategoriesDialog: React.FC<ExploreCategoriesDialogProps> = ({
  open,
  onClose,
  onSave,
  editingExploreCategory,
  setEditingExploreCategory,
  headerCategories = [],
  subcategories = [],
}) => {
  const { t } = useTranslation();

  const subcategoryOptions = useMemo(() => {
    const targetCategory = editingExploreCategory?.target_category_key;
    const filtered = targetCategory
      ? subcategories.filter((sub) => sub.category_key === targetCategory)
      : subcategories;

    const tree = subcategoriesService.buildTree(filtered);
    return subcategoriesService.flattenTree(tree);
  }, [subcategories, editingExploreCategory?.target_category_key]);

  const resolvedRoute = editingExploreCategory
    ? resolveExploreCategoryRoute(editingExploreCategory, subcategories)
    : null;

  const hasRouting =
    Boolean(editingExploreCategory?.link_url?.trim()) ||
    Boolean(editingExploreCategory?.target_category_key?.trim()) ||
    Boolean(editingExploreCategory?.category_key?.trim());

  const handleChange = (field: keyof CMSExploreCategory, value: unknown) => {
    if (!editingExploreCategory) return;
    setEditingExploreCategory({
      ...editingExploreCategory,
      [field]: value,
    });
  };

  const handleTargetCategoryChange = (categoryKey: string) => {
    if (!editingExploreCategory) return;
    setEditingExploreCategory({
      ...editingExploreCategory,
      target_category_key: categoryKey,
      target_subcategory_key: "",
    });
  };

  const handleSubcategoryChange = (subcategoryKey: string) => {
    if (!editingExploreCategory) return;
    const selected = subcategories.find((sub) => sub.key === subcategoryKey);
    setEditingExploreCategory({
      ...editingExploreCategory,
      target_subcategory_key: subcategoryKey,
      target_category_key: selected?.category_key || editingExploreCategory.target_category_key,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          width: { xs: "95%", sm: "90%", md: "80%" },
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle sx={{ fontSize: { xs: "1.25rem", sm: "1.5rem" } }}>
        {editingExploreCategory?.id
          ? t("admin.contentManagement.editExploreCategory")
          : t("admin.contentManagement.addExploreCategory")}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField
            label={t("admin.contentManagement.fields.cardKey")}
            fullWidth
            value={editingExploreCategory?.card_key || ""}
            onChange={(e) => handleChange("card_key", e.target.value)}
            helperText={t("admin.contentManagement.fields.cardKeyHelper")}
          />
          <TextField
            label={t("admin.contentManagement.fields.titleKey")}
            fullWidth
            value={editingExploreCategory?.title_key || ""}
            onChange={(e) => handleChange("title_key", e.target.value)}
            helperText={t("admin.contentManagement.fields.titleKeyHelper")}
          />
          <TextField
            label={t("admin.contentManagement.fields.titleEn")}
            fullWidth
            value={editingExploreCategory?.title_en || ""}
            onChange={(e) => handleChange("title_en", e.target.value)}
          />
          <TextField
            label={t("admin.contentManagement.fields.titleAr")}
            fullWidth
            value={editingExploreCategory?.title_ar || ""}
            onChange={(e) => handleChange("title_ar", e.target.value)}
          />
          <TextField
            label={t("admin.contentManagement.fields.titleFr")}
            fullWidth
            value={editingExploreCategory?.title_fr || ""}
            onChange={(e) => handleChange("title_fr", e.target.value)}
          />
          <ImageUpload
            currentImage={editingExploreCategory?.image_url || ""}
            onImageChange={(url: string) => handleChange("image_url", url)}
            folder="explore"
            label={t("admin.contentManagement.fields.categoryImage")}
          />

          <Typography variant="subtitle2" sx={{ mt: 1, fontWeight: 700 }}>
            {t("admin.contentManagement.fields.routingTarget")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("admin.contentManagement.fields.routingTargetHelper")}
          </Typography>

          <FormControl fullWidth>
            <InputLabel>{t("admin.contentManagement.fields.targetCategory")}</InputLabel>
            <Select
              value={editingExploreCategory?.target_category_key || ""}
              label={t("admin.contentManagement.fields.targetCategory")}
              onChange={(e) => handleTargetCategoryChange(e.target.value)}
            >
              <MenuItem value="">
                <em>{t("admin.contentManagement.fields.none")}</em>
              </MenuItem>
              {headerCategories.map((cat) => (
                <MenuItem key={cat.id} value={cat.key}>
                  {cat.label_en || cat.key}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth disabled={!editingExploreCategory?.target_category_key}>
            <InputLabel>{t("admin.contentManagement.fields.targetSubcategory")}</InputLabel>
            <Select
              value={editingExploreCategory?.target_subcategory_key || ""}
              label={t("admin.contentManagement.fields.targetSubcategory")}
              onChange={(e) => handleSubcategoryChange(e.target.value)}
            >
              <MenuItem value="">
                <em>{t("admin.contentManagement.fields.none")}</em>
              </MenuItem>
              {subcategoryOptions.map((sub) => (
                <MenuItem key={sub.id} value={sub.key}>
                  {sub.path}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label={t("admin.contentManagement.fields.linkUrl")}
            fullWidth
            value={editingExploreCategory?.link_url || ""}
            onChange={(e) => handleChange("link_url", e.target.value)}
            helperText={t("admin.contentManagement.fields.linkUrlHelper")}
          />

          {resolvedRoute && (
            <Alert severity="info" sx={{ py: 0.5 }}>
              {t("admin.contentManagement.fields.resolvedRoute")}:{" "}
              <Box component="span" sx={{ fontFamily: "monospace", fontWeight: 600 }}>
                {resolvedRoute}
              </Box>
            </Alert>
          )}

          {!hasRouting && (
            <Alert severity="warning">
              {t("admin.contentManagement.fields.routingRequired")}
            </Alert>
          )}

          <TextField
            label={t("admin.contentManagement.fields.displayOrder")}
            fullWidth
            type="number"
            value={editingExploreCategory?.display_order || 0}
            onChange={(e) => handleChange("display_order", parseInt(e.target.value, 10) || 0)}
          />
          <FormControlLabel
            control={
              <Switch
                checked={editingExploreCategory?.is_active ?? true}
                onChange={(e) => handleChange("is_active", e.target.checked)}
              />
            }
            label={t("admin.contentManagement.active")}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onClose}>
          {t("admin.contentManagement.cancel")}
        </Button>
        <Button
          variant="contained"
          onClick={onSave}
          disabled={!hasRouting}
          sx={{
            background: "linear-gradient(135deg, #9c27b0 0%, #7b1fa2 100%)",
          }}
        >
          {t("admin.contentManagement.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExploreCategoriesDialog;
